import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, Settings, Trash2, Users, Sparkles, Share2, Copy, Check, ShieldCheck, CreditCard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import EmojiPicker from "@/components/EmojiPicker";
import DailyTip from "@/components/DailyTip";
import ParentalConsentModal from "@/components/ParentalConsentModal";

const CHILD_COLORS = ["#FFB627", "#FF6B47", "#6FAE6D", "#5AA9E6", "#F38BA8", "#C58A6B"];

export default function Profiles() {
  const { user, logout, setRole } = useAuth();
  const nav = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");

  // create form state
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState("🌟");
  const [color, setColor] = useState(CHILD_COLORS[0]);
  const [agePack, setAgePack] = useState("mixed");
  const [creating, setCreating] = useState(false);
  // Synchronous double-submit guard. `creating` (React state) can lag
  // behind a fast second click because setState is batched, so a rapid
  // double-tap on "Create" was minting two profiles (Defect batch 3 #6,
  // 2026-07-05). This ref flips synchronously in the create handler.
  const submittingRef = useRef(false);
  // COPPA umbrella consent: pops on HTTP 423 from /profiles. Modal handles
  // the email-loop + KBA flow, then we auto-retry the create.
  const [consentPrompt, setConsentPrompt] = useState(null); // {attestationPreview, retryPayload} | null
  const [shareInfo, setShareInfo] = useState(null); // { profileId, url, copied }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/profiles");
      setProfiles(data);
      if (data.length === 0) setShowCreate(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const mintShare = async (profile) => {
    setShareInfo({ profileId: profile.id, url: "", copied: false, busy: true });
    try {
      const { data } = await api.post(`/profiles/${profile.id}/share`);
      const url = `${window.location.origin}/import/${data.token}`;
      setShareInfo({ profileId: profile.id, url, copied: false, busy: false });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setShareInfo(null);
    }
  };

  const copyShare = async () => {
    if (!shareInfo?.url) return;
    try {
      await navigator.clipboard.writeText(shareInfo.url);
      setShareInfo((s) => ({ ...s, copied: true }));
      setTimeout(() => setShareInfo((s) => s && { ...s, copied: false }), 1500);
    } catch {
      /* clipboard blocked — link is still selectable in the input */
    }
  };

  const closeShare = () => setShareInfo(null);

  const create = async (e) => {
    e.preventDefault();
    // Synchronous guard — see submittingRef comment above.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError("");
    setCreating(true);
    const payload = {
      name,
      avatar,
      color,
      voice_rate: 0.95,
      voice_pitch: 1.05,
      tile_size: "medium",
      age_pack: agePack,
    };
    try {
      const { data } = await api.post("/profiles", payload);
      setProfiles((p) => [...p, data]);
      setName(""); setAvatar("🌟"); setColor(CHILD_COLORS[0]); setAgePack("mixed");
      setShowCreate(false);
      nav(`/board/${data.id}`);
    } catch (err) {
      // STRICT trigger: only the specific COPPA shape opens the modal.
      // Other 423s, validation errors, 401/402/500 fall through to the
      // existing error banner.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 423 && detail?.code === "parental_consent_required") {
        setConsentPrompt({
          attestationPreview: detail.attestation_preview || "",
          retryPayload: payload,
        });
      } else {
        setError(formatApiErrorDetail(detail) || err.message);
      }
    }
    setCreating(false);
    submittingRef.current = false;
  };

  // Called by ParentalConsentModal the moment status-poll detects consent.
  const onConsentRecorded = async () => {
    const retryPayload = consentPrompt?.retryPayload;
    setConsentPrompt(null);
    if (!retryPayload) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setCreating(true);
    try {
      const { data } = await api.post("/profiles", retryPayload);
      setProfiles((p) => [...p, data]);
      setName(""); setAvatar("🌟"); setColor(CHILD_COLORS[0]); setAgePack("mixed");
      setShowCreate(false);
      nav(`/board/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
      submittingRef.current = false;
    }
  };

  // Note: board deletion moved to ManageBoard → Parental settings →
  // Danger zone (Defect-batch-2 #4, 2026-07-04). This page no longer
  // exposes any destructive affordance.

  return (
    <div className="min-h-screen px-4 sm:px-8 py-8 dotted-bg">
      <div className="max-w-5xl mx-auto">
        <header className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div>
            <div className="display text-sm uppercase tracking-[0.25em] text-[color:var(--ink-soft)]">
              {user?.role === "therapist" ? "Therapist" : "Caregiver"}
            </div>
            <h1 className="display text-3xl sm:text-4xl font-extrabold" data-testid="profiles-greeting">
              Hi, {user?.name?.split(" ")[0] || "there"} 👋
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Link to="/admin/verify" className="pill-btn" data-testid="profiles-admin">
                <ShieldCheck size={14} /> Admin
              </Link>
            )}
            <Link to="/pricing" className="pill-btn" data-testid="profiles-pricing">
              <CreditCard size={14} /> Plan
            </Link>
            {user?.role === "therapist" ? (
              <Link to="/clients" className="pill-btn gold" data-testid="profiles-clients">
                <Users size={16} /> Client dashboard
              </Link>
            ) : user?.role === "admin" ? null : (
              <button
                type="button"
                onClick={async () => {
                  if (window.confirm(
                    "Switch to Therapist mode?\n\nUnlocks the multi-client dashboard, weekly stats per child, and branded exports. Early access."
                  )) {
                    const r = await setRole("therapist");
                    if (r.ok) nav("/clients");
                  }
                }}
                className="pill-btn"
                data-testid="profiles-become-therapist"
              >
                <Sparkles size={14} /> I'm a therapist
              </button>
            )}
            <button
              type="button"
              onClick={async () => { await logout(); nav("/"); }}
              className="pill-btn"
              data-testid="profiles-logout"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </header>

        <h2 className="display text-2xl font-bold mb-3">Communication boards</h2>
        <p className="text-[color:var(--ink-soft)] mb-6 max-w-xl">
          Each board belongs to one person. Create one for your child or anyone you support — then add the words and pictures that matter to them.
        </p>

        {error && (
          <div className="brutal-card bg-white p-3 mb-4 text-[color:var(--coral-deep)]" data-testid="profiles-error">
            {error}
          </div>
        )}

        {/* Refer-a-friend promo removed per R4-G: no signup credits, no promo
            credits, ever — founder-discretionary grants are personal, not a
            surface. */}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {profiles.map((p) => (
            <div key={p.id} className="brutal-card p-5 bg-white relative" data-testid={`profile-card-${p.id}`}>
              {p.helper && (
                <div
                  className="absolute -top-2 -left-2 bg-[color:var(--gold)] border-[3px] border-[color:var(--ink)] rounded-full px-3 py-0.5 text-[10px] uppercase tracking-[0.18em] font-extrabold shadow-[2px_2px_0_0_var(--ink)]"
                  data-testid={`profile-helper-badge-${p.id}`}
                >
                  ★ Helping · {p.helper.role.replace(/_/g, " ")}
                </div>
              )}
              {!p.helper && (
                <div className="absolute top-3 right-3 flex gap-1">
                  <button
                    type="button"
                    onClick={() => mintShare(p)}
                    className="bg-[color:var(--paper)] border-[3px] border-[color:var(--ink)] rounded-full w-9 h-9 flex items-center justify-center shadow-[2px_2px_0_0_var(--ink)]"
                    aria-label="Share board"
                    title="Share this board with another caregiver"
                    data-testid={`profile-share-${p.id}`}
                  >
                    <Share2 size={15} />
                  </button>
                  <Link
                    to={`/settings/${p.id}`}
                    className="bg-[color:var(--gold)] border-[3px] border-[color:var(--ink)] rounded-full w-9 h-9 flex items-center justify-center shadow-[2px_2px_0_0_var(--ink)]"
                    aria-label="Settings"
                    data-testid={`profile-settings-${p.id}`}
                  >
                    <Settings size={15} />
                  </Link>
                  {/* Board deletion moved to ManageBoard → Parental settings →
                      Danger zone (Defect-batch-2 #4, 2026-07-04). No destructive
                      affordances live in the normal profile-picker flow. */}
                </div>
              )}
              <div
                className="w-20 h-20 rounded-2xl border-[3px] border-[color:var(--ink)] flex items-center justify-center text-5xl mb-3"
                style={{ background: p.color }}
              >
                {p.avatar}
              </div>
              <div className="display text-2xl font-bold">{p.name}</div>
              <div className="text-sm text-[color:var(--ink-soft)] mb-3">
                {p.helper
                  ? <>Can: {p.helper.permissions.join(", ")}</>
                  : <>Voice: rate {p.voice_rate.toFixed(2)} · pitch {p.voice_pitch.toFixed(2)}</>
                }
              </div>
              {!p.helper && <DailyTip profileId={p.id} />}
              <Link
                to={`/board/${p.id}`}
                className="pill-btn primary inline-flex"
                data-testid={`profile-open-${p.id}`}
              >
                Open board
              </Link>
            </div>
          ))}

          {!showCreate && !loading && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="brutal-card p-5 bg-transparent flex flex-col items-center justify-center min-h-[210px] cursor-pointer hover:bg-[color:var(--cream-deep)]"
              style={{ borderStyle: "dashed" }}
              data-testid="profile-add-card"
            >
              <Plus size={40} />
              <div className="display text-xl font-bold mt-2">Add board</div>
            </button>
          )}
        </div>

        {showCreate && (
          <form onSubmit={create} className="brutal-card p-6 bg-white mt-6 max-w-xl" data-testid="profile-create-form">
            <h3 className="display text-2xl font-bold mb-3">New communication board</h3>
            <label className="block mb-3">
              <span className="display font-semibold mb-1 block">Name</span>
              <input
                className="brutal-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="e.g. Sammy"
                maxLength={60}
                data-testid="profile-name-input"
              />
            </label>
            <div className="mb-3">
              <span className="display font-semibold mb-1 block">Avatar</span>
              <EmojiPicker value={avatar} onChange={setAvatar} />
            </div>
            <div className="mb-5">
              <span className="display font-semibold mb-1 block">Color</span>
              <div className="flex gap-2 flex-wrap">
                {CHILD_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-12 h-12 rounded-full border-[3px] border-[color:var(--ink)] ${color === c ? "ring-4 ring-[color:var(--gold)]" : ""}`}
                    style={{ background: c }}
                    aria-label={`Color ${c}`}
                    data-testid={`profile-color-${c}`}
                  />
                ))}
              </div>
            </div>
            <div className="mb-5">
              <span className="display font-semibold mb-1 block">Age range</span>
              <p className="text-sm text-[color:var(--ink-soft)] mb-2">
                Pre-loads the right starter vocabulary. You can change every tile later.
              </p>
              <div className="flex flex-wrap gap-2" data-testid="profile-age-pack">
                {[
                  { id: "toddler", label: "Toddler", sub: "2 – 4 yrs", emoji: "🧸" },
                  { id: "school", label: "School-age", sub: "5 – 12 yrs", emoji: "🎒" },
                  { id: "teen", label: "Teen / adult", sub: "13+", emoji: "🛣️" },
                  { id: "mixed", label: "Mixed", sub: "All ages", emoji: "✨" },
                ].map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setAgePack(p.id)}
                    className={`pill-btn ${agePack === p.id ? "gold" : ""}`}
                    aria-pressed={agePack === p.id}
                    data-testid={`profile-age-pack-${p.id}`}
                  >
                    <span style={{ fontSize: 18 }} aria-hidden>{p.emoji}</span>
                    <span className="font-semibold">{p.label}</span>
                    <span className="text-xs text-[color:var(--ink-soft)]">{p.sub}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="pill-btn"
                data-testid="profile-create-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="pill-btn primary"
                disabled={creating}
                data-testid="profile-create-submit"
              >
                {creating ? "Creating…" : "Create board"}
              </button>
            </div>
          </form>
        )}

        {/* Discovery footer — surfaces public marketing pages while inside the app */}
        <nav className="mt-12 pt-6 border-t-[3px] border-dashed border-[color:var(--paper-muted)] flex flex-wrap gap-x-4 gap-y-2 items-center" data-testid="profiles-discover-nav">
          <span className="display text-xs uppercase tracking-[0.25em] text-[color:var(--ink-soft)] font-bold mr-2">Explore</span>
          <Link to="/about" className="text-sm underline" data-testid="discover-about">Our story</Link>
          <Link to="/community" className="text-sm underline" data-testid="discover-community">Community stories</Link>
          <Link to="/family-story" className="text-sm underline" data-testid="discover-family-story">Share your story</Link>
          <Link to="/blog" className="text-sm underline" data-testid="discover-blog">Journal</Link>
          <Link to="/therapists" className="text-sm underline" data-testid="discover-therapists">Find a therapist</Link>
          <Link to="/press" className="text-sm underline" data-testid="discover-press">Press kit</Link>
          <Link to="/privacy" className="text-sm underline" data-testid="discover-privacy">Privacy</Link>
          <Link to="/privacy/me" className="text-sm underline font-bold" data-testid="discover-privacy-me">My data</Link>
        </nav>
      </div>

      {/* Share modal */}
      {shareInfo && (
        <div className="modal-overlay" onClick={closeShare} data-testid="share-modal">
          <div
            className="brutal-card bg-white p-6 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="display text-xl font-bold mb-2 flex items-center gap-2">
              <Share2 size={18} className="text-[color:var(--coral)]" />
              Share this board
            </h2>
            <p className="text-[color:var(--ink-soft)] mb-3">
              Send this link to a caregiver, therapist, or family member. They'll get a one-tap import to copy
              the categories and tiles into their own account. Custom photos and voice recordings are
              <strong> not </strong>copied — that stays private to your child.
            </p>
            {shareInfo.busy ? (
              <div className="display text-lg">Generating link…</div>
            ) : shareInfo.url ? (
              <>
                <div className="flex gap-2 mb-3">
                  <input
                    className="brutal-input flex-1"
                    readOnly
                    value={shareInfo.url}
                    onFocus={(e) => e.target.select()}
                    data-testid="share-url"
                  />
                  <button
                    type="button"
                    onClick={copyShare}
                    className={`pill-btn ${shareInfo.copied ? "gold" : "primary"}`}
                    data-testid="share-copy"
                  >
                    {shareInfo.copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy</>}
                  </button>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={async () => {
                      await api.delete(`/profiles/${shareInfo.profileId}/share`);
                      closeShare();
                    }}
                    className="pill-btn danger"
                    data-testid="share-revoke"
                  >
                    <Trash2 size={14} /> Revoke
                  </button>
                  <button
                    type="button"
                    onClick={closeShare}
                    className="pill-btn"
                    data-testid="share-done"
                  >
                    Done
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
      {consentPrompt && (
        <ParentalConsentModal
          childFirstName={name}
          attestationPreview={consentPrompt.attestationPreview}
          returnTo="/profiles"
          onClose={() => setConsentPrompt(null)}
          onConsentRecorded={onConsentRecorded}
        />
      )}
    </div>
  );
}
